import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";
import * as Yup from "yup";
import {
  createProperty,
  fetchAllPropertyCodes,
  fetchPropertyByCode,
  openPropertyForm,
  updateProperty,
} from "../../../redux/slices/property";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  MenuItem,
  RadioGroup,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Typography,
  styled,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Formik } from "formik";
import { Grid, Radio } from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { fetchUsers } from "../../../redux/slices/user";
import { a11yProps } from "../CustomTabs";
import { useEffect, useState, useMemo } from "react";
import { MapComponent } from "../mapComponents/MapComponent";
import { Helmet } from "react-helmet-async";
import React from "react";
import { spacing } from "@mui/system";
import * as turf from "@turf/turf";
import { AppDispatch } from "../../../redux/store";
import { Add, Remove } from "@mui/icons-material";
import { getPropertyName } from "../../../services/maanmittauslaitosService";
import { WGS84lalToETRSTM35FINxy } from "../../../utils/coordinateConverter";

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

// Traffic type coefficients
const TRAFFIC_COEFFICIENTS: Record<string, number> = {
  ula: 1700,
  ull_1300: 1300,
  ull_750: 750,
  ull_350: 350,
  slv_60: 60,
  slv_130: 130,
  mel_21: 21,
  mel_18: 18,
  mel_11: 11,
  mel_7: 7,
  mel_3: 3,
  muu: 0,
  kayttomaksu: 0,
};

// ULL sub-categories
const ULL_CATEGORIES = [
  { value: "ull_1300", label: "ULL - Lomamökki ympärivuotinen (1300)" },
  { value: "ull_750", label: "ULL - Kesämökki (750)" },
  { value: "ull_350", label: "ULL - Lomamökki (350)" },
];

const SLV_CATEGORIES = [
  { value: "slv_60", label: "SLV - Kasvinviljely (60)" },
  { value: "slv_130", label: "SLV - Nautakarja (130)" },
];

// MeL sub-categories (different areas)
const MEL_CATEGORIES = [
  { value: "mel_21", label: "MeL - Alue 1 (21)" },
  { value: "mel_18", label: "MeL - Alue 2 (18)" },
  { value: "mel_11", label: "MeL - Alue 3 (11)" },
  { value: "mel_7", label: "MeL - Alue 4 (7)" },
  { value: "mel_3", label: "MeL - Alue 5 (3)" },
];

// src/components/PropertyForm.tsx
type PropertyFormProps = {
  initialData: any; // tai tarkempi tyyppi
  isEditMode: boolean;
  onSuccess: () => void;
};

type UnitizationRow = {
  id: string;
  trafficType: string;
  hectareCoefficient: string;
  trafficWeightCoefficient: string;
  weightCoefficient: string;
  distance: string;
  comments: string;
  weightValue: number;
  kayttomaksuAmount: string;
  muuTripCount: string;
  muuVehicleWeight: string;
  muuPayload: string;
};

export const validatePropertyCodeFormat = (
  code: string
): string | undefined => {
  if (!code) return "Kiinteistötunnus on pakollinen";

  const parts = code.split("-");
  if (parts.length !== 4) {
    return "Kiinteistötunnuksen tulee sisältää neljä osaa väliviivoilla erotettuna";
  }

  if (!parts.every((part) => /^\d{1,3}$/.test(part))) {
    return "Jokainen osa koodissa saa sisältää vain 1-3 numeroa";
  }

  return undefined;
};

export const validatePropertyCode = async (
  code: string,
  dispatch: AppDispatch,
  propertyId?: number
): Promise<string | undefined> => {
  // First validate format without any fetch calls
  const formatError = validatePropertyCodeFormat(code);
  if (formatError) return formatError;

  // Only make fetch calls if format is valid
  try {
    const allProperties: { id: number; code: string }[] = await dispatch(
      fetchAllPropertyCodes()
    ).unwrap();

    const duplicate = allProperties.find(
      (p) => p.code.trim().toLowerCase() === code.trim().toLowerCase()
    );

    // toistaiseksi saa lisätä duplikaatteja
    // if (duplicate && duplicate.id !== propertyId) {
    //   return "Kiinteistö on jo lisätty tietokantaan";
    // }
  } catch (err) {
    console.error("Kiinteistötunnuksen haku epäonnistui:", err);
    return "Virhe tarkistettaessa koodia";
  }

  return undefined;
};

export default function PropertyForm({
  initialData,
  isEditMode,
  onSuccess,
}: PropertyFormProps) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const property = useAppSelector((state) => state.property.property);
  const { users } = useAppSelector((state) => state.user);
  const { properties } = useAppSelector((state) => state.property);
  const { roads } = useAppSelector((state) => state.road);
  const [value, setValue] = useState(0);
  const [area, setArea] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const navigate = useNavigate();
  const [unitizationMode, setUnitizationMode] = useState<"easy" | "advanced">(
    "easy"
  );
  const [unitizationRows, setUnitizationRows] = useState<UnitizationRow[]>([
    {
      id: crypto.randomUUID(),
      trafficType: "",
      hectareCoefficient: "1",
      trafficWeightCoefficient: "1",
      weightCoefficient: "100",
      distance: "1",
      comments: "",
      weightValue: 0,
      kayttomaksuAmount: "0",
      muuTripCount: "0",
      muuVehicleWeight: "0",
      muuPayload: "0",
    },
  ]);
  const [showModeChangeDialog, setShowModeChangeDialog] = useState(false);
  const [pendingModeChange, setPendingModeChange] = useState<{
    mode: "easy" | "advanced";
    formValues: any;
  } | null>(null);

  useEffect(() => {
    if (
      initialData?.propertyGeometries?.length &&
      initialData.propertyGeometries[0].geom?.type === "Polygon"
    ) {
      const areaM2 = turf.area(initialData.propertyGeometries[0].geom);
      const roundedAreaM2 = Math.round(areaM2);
      setArea(roundedAreaM2);
    }
  }, [initialData]);

  useEffect(() => {
    // Set unitization mode from backend
    if (initialData?.unitizationMode) {
      setUnitizationMode(initialData.unitizationMode);
    }

    // Load existing unitization rows if available
    // This includes easy mode properties so users can switch to advanced and see existing data
    if (
      initialData?.unitizationRows &&
      initialData.unitizationRows.length > 0
    ) {
      setUnitizationRows(initialData.unitizationRows);
    }
  }, [initialData]);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    if (!currentUser?.associations?.length) return;
    if (!associationId) return;

    // Fetch only users with MEMBER role for property ownership dropdown
    dispatch(fetchUsers({ associationId, role: "MEMBER" }));
  }, [dispatch, associationId]);

  // Dynamic validation schema based on unitization mode
  const getValidationSchema = (mode: "easy" | "advanced") => {
    return Yup.object().shape({
      name: Yup.string().required("Nimi on pakollinen"),
      code: Yup.string().required("Kiinteistötunnus on pakollinen"),
      user: Yup.number()
        .typeError("Valitse käyttäjä")
        .required("Käyttäjä on pakollinen"),
      units:
        mode === "easy"
          ? Yup.number()
              .typeError("Anna yksikkömäärä numerona")
              .min(0, "Yksiköiden tulee olla vähintään 0")
              .required("Yksiköt ovat pakollinen")
          : Yup.number()
              .typeError("Anna yksikkömäärä numerona")
              .min(0, "Yksiköiden tulee olla vähintään 0")
              .notRequired(),
    });
  };

  // Memoize the validation schema to prevent unnecessary re-creation
  const validationSchema = useMemo(
    () => getValidationSchema(unitizationMode),
    [unitizationMode]
  );

  const handleCancel = () => {
    dispatch(openPropertyForm(false));
    navigate("/properties");
  };

  const handleUnitizationModeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    formValues: any
  ) => {
    const mode = event.target.value as "easy" | "advanced";

    // If switching from advanced to easy and there are multiple rows or usage fees, warn user
    if (
      mode === "easy" &&
      unitizationMode === "advanced" &&
      unitizationRows &&
      unitizationRows.length > 1
    ) {
      // Show confirmation dialog
      setPendingModeChange({ mode, formValues });
      setShowModeChangeDialog(true);
      return;
    }

    // Check if any row has käyttömaksu
    const hasUsageFees =
      unitizationRows &&
      unitizationRows.some((row) => row.trafficType === "kayttomaksu");
    if (mode === "easy" && unitizationMode === "advanced" && hasUsageFees) {
      // Show confirmation dialog
      setPendingModeChange({ mode, formValues });
      setShowModeChangeDialog(true);
      return;
    }

    // Proceed with mode change
    applyModeChange(mode, formValues);
  };

  const applyModeChange = (mode: "easy" | "advanced", formValues: any) => {
    setUnitizationMode(mode);

    // If switching to advanced mode and there are no proper rows yet (new property),
    // create initial MUU row from units value if available
    if (
      mode === "advanced" &&
      unitizationRows &&
      unitizationRows.length === 1 &&
      unitizationRows[0].trafficType === "" &&
      formValues.units
    ) {
      setUnitizationRows([
        {
          id: crypto.randomUUID(),
          trafficType: "muu",
          hectareCoefficient: "1",
          trafficWeightCoefficient: "0",
          weightCoefficient: "100",
          distance: "1",
          comments: "",
          weightValue: parseFloat(formValues.units) || 0,
          kayttomaksuAmount: "0",
          muuTripCount: "1",
          muuVehicleWeight: formValues.units.toString(),
          muuPayload: "0",
        },
      ]);
    }

    // If switching to easy mode with multiple rows, reset to default single row
    if (mode === "easy" && unitizationRows && unitizationRows.length > 1) {
      setUnitizationRows([
        {
          id: crypto.randomUUID(),
          trafficType: "",
          hectareCoefficient: "1",
          trafficWeightCoefficient: "1",
          weightCoefficient: "100",
          distance: "1",
          comments: "",
          weightValue: 0,
          kayttomaksuAmount: "0",
          muuTripCount: "0",
          muuVehicleWeight: "0",
          muuPayload: "0",
        },
      ]);
    }

    // If advanced mode is selected, switch to "Yksiköinti" tab (index 2)
    if (mode === "advanced") {
      setValue(2);
    }
  };

  const handleConfirmModeChange = () => {
    if (pendingModeChange) {
      applyModeChange(pendingModeChange.mode, pendingModeChange.formValues);
    }
    setShowModeChangeDialog(false);
    setPendingModeChange(null);
  };

  const handleCancelModeChange = () => {
    setShowModeChangeDialog(false);
    setPendingModeChange(null);
  };

  const handleBackToForm = () => {
    setValue(0);
  };

  const handleAddUnitizationRow = () => {
    const newRow: UnitizationRow = {
      id: crypto.randomUUID(),
      trafficType: "",
      hectareCoefficient: "1",
      trafficWeightCoefficient: "1",
      weightCoefficient: "100",
      distance: "1",
      comments: "",
      weightValue: 0,
      kayttomaksuAmount: "0",
      muuTripCount: "0",
      muuVehicleWeight: "0",
      muuPayload: "0",
    };
    setUnitizationRows([...unitizationRows, newRow]);
  };

  const handleDeleteUnitizationRow = (id: string) => {
    // Keep at least one row
    if (unitizationRows.length > 1) {
      setUnitizationRows(unitizationRows.filter((row) => row.id !== id));
    }
  };

  const calculateWeightValue = (row: UnitizationRow): number => {
    // Special formula for "muu" type: tripCount * (vehicleWeight + payload)
    if (row.trafficType === "muu") {
      const tripCount = parseFloat(row.muuTripCount) || 0;
      const vehicleWeight = parseFloat(row.muuVehicleWeight) || 0;
      const payload = parseFloat(row.muuPayload) || 0;
      const distance = parseFloat(row.distance) || 0;
      return (tripCount * vehicleWeight + tripCount * payload) * distance;
    }

    // Formula: hectareCoefficient * trafficWeightCoefficient * weightCoefficient * distance
    // Distance is in kilometers
    const hectareCoeff = parseFloat(row.hectareCoefficient) || 0;
    const trafficWeightCoeff = parseFloat(row.trafficWeightCoefficient) || 0;
    const weightCoeff = parseFloat(row.weightCoefficient) || 0;
    const distance = parseFloat(row.distance) || 0;

    return hectareCoeff * trafficWeightCoeff * (weightCoeff / 100) * distance;
  };

  const handleUnitizationRowChange = (
    id: string,
    field: keyof UnitizationRow,
    value: string | number
  ) => {
    setUnitizationRows(
      unitizationRows.map((row) => {
        if (row.id !== id) return row;

        // Handle numeric fields - keep as string but replace commas with dots
        let processedValue = value;
        if (
          typeof value === "string" &&
          (field === "hectareCoefficient" ||
            field === "trafficWeightCoefficient" ||
            field === "weightCoefficient" ||
            field === "distance" ||
            field === "kayttomaksuAmount" ||
            field === "muuTripCount" ||
            field === "muuVehicleWeight" ||
            field === "muuPayload")
        ) {
          // Replace commas with dots for decimal input
          processedValue = value.replace(/,/g, ".");
        }

        let updatedRow = { ...row, [field]: processedValue };

        // If traffic type changed, update the traffic weight coefficient
        if (field === "trafficType" && typeof value === "string") {
          updatedRow.trafficWeightCoefficient = String(
            TRAFFIC_COEFFICIENTS[value] || 0
          );
        }

        // Recalculate weight value after any change
        updatedRow.weightValue = calculateWeightValue(updatedRow);

        return updatedRow;
      })
    );
  };

  const handleSubmit = async (
    values: any,
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      let unitizationData = undefined;

      if (unitizationMode === "advanced") {
        unitizationData = unitizationRows;
      } else if (unitizationMode === "easy" && values.units) {
        // Create a MUU unitization row from the units value
        // Store units as muuVehicleWeight so formula: tripCount * (vehicleWeight + payload) = 1 * (units + 0) = units
        unitizationData = [
          {
            id: crypto.randomUUID(),
            trafficType: "muu",
            hectareCoefficient: "1",
            trafficWeightCoefficient: "0",
            weightCoefficient: "100",
            distance: "1",
            comments: "Perusyksiköinti",
            weightValue: parseFloat(values.units) || 0,
            kayttomaksuAmount: "0",
            muuTripCount: "1",
            muuVehicleWeight: values.units.toString(),
            muuPayload: "0",
          },
        ];
      }

      // Calculate units based on mode
      let finalUnits = values.units;

      if (unitizationMode === "advanced") {
        // In advanced mode, units = sum of all unitization row weight values
        finalUnits = unitizationRows.reduce(
          (sum, row) => sum + row.weightValue,
          0
        );
      } else {
        // In easy mode, use the form value (or 0 if empty)
        finalUnits = values.units === "" ? 0 : values.units;
      }

      const updatedValues = {
        ...values,
        units: finalUnits,
        area,
        unitizationRows: unitizationData,
      };

      if (isEditMode && initialData?.id) {
        await dispatch(updateProperty(initialData.id, updatedValues));
      } else {
        await dispatch(createProperty(updatedValues));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      onSuccess();
    } catch (error) {
      setStatus({ sent: false });
      setErrors({ submit: "Virhe tallennuksessa" });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={
        initialData
          ? {
              name: initialData.name,
              code: initialData.code,
              address: initialData.address,
              area: area,
              user: initialData.user?.id || "",
              road:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id ??
                "",
              units: initialData.units,
              propertyGeometries: initialData?.propertyGeometries ?? [],
            }
          : {
              name: "",
              code: "",
              address: "",
              area: "100",
              user: "",
              road:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id ??
                "",
              units: "",
              propertyGeometries: initialData?.propertyGeometries ?? [],
            }
      }
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      // validate={async (values) => {
      //   const errors: { code?: string } = {};
      //   const codeError = await validatePropertyCode(
      //     values.code,
      //     dispatch,
      //     property?.id
      //   );

      //   if (codeError) {
      //     errors.code = codeError;
      //   } else {
      //     try {
      //       const feature = await dispatch(
      //         fetchPropertyByCode(values.code)
      //       ).unwrap();
      //       const areaM2 = turf.area(feature.geometry);
      //       setArea(Math.round(areaM2));
      //     } catch (err) {
      //       console.error("Kiinteistön haku epäonnistui:", err);
      //       setArea(0);
      //       errors.code = "Kiinteistötunnuksella ei löytynyt tietoja";
      //     }
      //   }
      //   return errors;
      // }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => {
        const centerCoords =
          values.propertyGeometries?.[0]?.geometry?.coordinates?.[0]?.[0] ??
          values.propertyGeometries?.[0]?.properties
            ?.kiinteistotunnuksenSijainti?.coordinates ??
          property?.propertyGeometries?.[0]?.geom?.coordinates?.[0]?.[0] ??
          property?.propertyGeometries?.[0]?.location?.coordinates;

        const handleTabChange = (
          event: React.SyntheticEvent,
          newValue: number
        ) => {
          // Prevent switching to map or yksiköinti tabs without property code
          if (!checkPropertyGeometryExists(values) && newValue === 2) {
            alert(
              "Kiinteistötunnus puuttuu. Hae kiinteistötunnus ennen jatkamista."
            );
            return; // estää tabin vaihdon
          }
          setValue(newValue);
        };

        const checkPropertyGeometryExists = (values: any): boolean => {
          return (
            values.propertyGeometries && values.propertyGeometries.length > 0
          );
        };

        const handleCodeBlur = async (
          e: React.FocusEvent<HTMLInputElement>
        ) => {
          handleBlur(e);

          if (!/^\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}$/.test(values.code)) return;

          try {
            const feature = await dispatch(
              fetchPropertyByCode(values.code)
            ).unwrap();

            // Lasketaan pinta-ala
            const areaM2 = turf.area(feature.geometry);
            setArea(Math.round(areaM2));

            // Muutetaan feature vanhan rakenteen mukaiseksi
            const coords =
              feature.properties?.kiinteistotunnuksenSijainti?.coordinates;
            const harmonizedFeature = {
              ...feature,
              geom: feature.geometry, // Polygon tallennetaan 'geom'-kenttään
              location: {
                type: "Point",
                coordinates:
                  coords ||
                  turf.centroid(feature.geometry).geometry.coordinates,
              },
            };

            setFieldValue("propertyGeometries", [harmonizedFeature]);

            // Hae kiinteistön nimi — coords on WGS84 [lng, lat], muunna ETRS-TM35FIN:ksi
            if (coords) {
              const etrs = await WGS84lalToETRSTM35FINxy({
                Lo: coords[0],
                La: coords[1],
              });
              const name = await getPropertyName(etrs.E, etrs.N);
              if (name) setFieldValue("name", name);
            }
          } catch (err) {
            console.error("Kiinteistön haku epäonnistui:", err);
            setArea(0);
            setFieldValue("propertyGeometries", []); // tyhjennä jos haku epäonnistui
          }
        };

        return (
          <React.Fragment>
            <Helmet title="Kiinteistöt" />
            <Typography variant="h3" gutterBottom display="inline">
              Kiinteistöt
            </Typography>
            <Breadcrumbs aria-label="Breadcrumb" sx={{ mt: 2 }}>
              <Link component={NavLink} to="/">
                Etusivu
              </Link>
              <Typography>Kiinteistöt</Typography>
            </Breadcrumbs>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ borderBottom: 1, mb: 3, borderColor: "divider" }}>
                  <Typography variant="h6" gutterBottom>
                    Kiinteistöt
                  </Typography>
                  <Tabs
                    value={value}
                    onChange={handleTabChange}
                    aria-label="basic tabs example"
                  >
                    <Tab
                      label={
                        !isEditMode
                          ? "Lisää lomakkeella"
                          : "Muokkaa lomakkeella"
                      }
                      {...a11yProps(0)}
                    />
                    <Tab
                      label={!isEditMode ? "Lisää kartasta" : "Näytä kartalla"}
                      {...a11yProps(1)}
                    />
                    <Tab label="Yksiköinti" {...a11yProps(2)} />
                  </Tabs>
                </Box>

                {value === 0 && (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={6}>
                      <Grid item md={6}>
                        <TextField
                          name="name"
                          label="Nimi"
                          value={values.name}
                          error={Boolean(touched.name && errors.name)}
                          fullWidth
                          helperText={
                            touched.name && typeof errors.name === "string"
                              ? errors.name
                              : undefined
                          }
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          my={3}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          name="code"
                          label="Kiinteistötunnus"
                          placeholder="999-999-9-99"
                          value={values.code}
                          // The Formik error state will now handle this
                          error={Boolean(touched.code && errors.code)}
                          fullWidth
                          // No custom logic here, just pass the Formik error
                          helperText={
                            touched.code && errors.code
                              ? (errors.code as string)
                              : undefined
                          }
                          onBlur={handleCodeBlur}
                          onChange={handleChange}
                          variant="outlined"
                          my={3}
                        />
                      </Grid>

                      <Grid item md={6}>
                        <TextField
                          name="area"
                          label="Kiinteistön pinta-ala"
                          value={
                            area >= 10000
                              ? `${(area / 10000).toFixed(2)} ha`
                              : `${area} m²`
                          }
                          fullWidth
                          disabled
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item md={6} xs={6}>
                        <TextField
                          select
                          name="user"
                          label="Osakas"
                          value={values.user}
                          error={Boolean(touched.user && errors.user)}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="text"
                          variant="outlined"
                        >
                          {users.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.firstName + " " + user.lastName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item md={12}>
                        <FormControl>
                          <FormLabel id="demo-row-radio-buttons-group-label">
                            Yksiköinti
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="unitization-mode"
                            value={unitizationMode}
                            onChange={(e) => {
                              if (!checkPropertyGeometryExists(values)) {
                                alert(
                                  "Kiinteistötunnus puuttuu. Syötä kiinteistötunnus ennen edistyneeseen tilaan siirtymistä."
                                );
                                return;
                              }
                              handleUnitizationModeChange(e, values);
                            }}
                          >
                            <FormControlLabel
                              value="easy"
                              control={<Radio />}
                              label="Helppo"
                            />
                            <FormControlLabel
                              value="advanced"
                              control={<Radio />}
                              label="Edistynyt"
                            />
                          </RadioGroup>
                        </FormControl>
                        {unitizationMode === "easy" && (
                          <TextField
                            name="units"
                            label="Yksiköt"
                            placeholder="1000"
                            value={values.units}
                            error={Boolean(touched.units && errors.units)}
                            fullWidth
                            helperText={
                              touched.units && typeof errors.units === "string"
                                ? errors.units
                                : undefined
                            }
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={3}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ mt: 3, mr: 2 }}
                      disabled={isSubmitting}
                    >
                      Tallenna
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ mt: 3 }}
                      onClick={handleCancel}
                    >
                      Peruuta
                    </Button>
                  </form>
                )}
                {value === 1 && (
                  <Box mt={3}>
                    {isEditMode ? (
                      <MapComponent
                        propertyGeometries={property?.propertyGeometries}
                        type={"propertyGeometries"}
                      />
                    ) : (
                      <MapComponent
                        properties={properties}
                        roads={roads}
                        type={"properties"}
                      />
                    )}
                  </Box>
                )}
                {value === 2 && (
                  <Box mt={3}>
                    <Box
                      sx={{
                        position: "relative",
                        height: expanded ? "520px" : "270px",
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 3,
                        transition: "height 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <MapComponent
                          propertyGeometries={
                            values.propertyGeometries?.length > 0
                              ? values.propertyGeometries
                              : property?.propertyGeometries
                          }
                          type="propertyGeometries"
                          centerCoords={centerCoords}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setExpanded((prev) => !prev)}
                        sx={{
                          position: "absolute",
                          bottom: 12,
                          right: 12,
                          borderRadius: "9999px",
                          boxShadow: 2,
                          bgcolor: "white",
                          color: "black",
                          "&:hover": { bgcolor: "grey.200" },
                        }}
                      >
                        {expanded ? "Pienennä" : "Laajenna"}
                      </Button>
                    </Box>
                    <Box mt={3}>
                      {unitizationRows.map((row, index) => (
                        <Grid
                          key={row.id}
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ mb: 3 }}
                        >
                          <Grid item md={1}>
                            <TextField
                              select
                              label="Liikennelaji"
                              value={row.trafficType}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "trafficType",
                                  e.target.value
                                )
                              }
                              fullWidth
                              variant="outlined"
                            >
                              <MenuItem value={"ula"}>
                                ULA - Asuinkiinteistö
                              </MenuItem>
                              {ULL_CATEGORIES.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                              {SLV_CATEGORIES.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                              {MEL_CATEGORIES.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                              <MenuItem value={"muu"}>Muu</MenuItem>
                              <MenuItem value={"kayttomaksu"}>
                                Käyttömaksu
                              </MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Hehtaarikerroin"
                              value={row.hectareCoefficient}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "hectareCoefficient",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={
                                row.trafficType === "ula" ||
                                row.trafficType.startsWith("ull_") ||
                                row.trafficType === "kayttomaksu" ||
                                row.trafficType === "muu"
                              }
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Liikennelaji painokerroin"
                              value={row.trafficWeightCoefficient}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "trafficWeightCoefficient",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={
                                row.trafficType === "kayttomaksu" ||
                                row.trafficType === "muu"
                              }
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Korjauskerroin (%)"
                              value={row.weightCoefficient}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "weightCoefficient",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={
                                row.trafficType === "kayttomaksu" ||
                                row.trafficType === "muu"
                              }
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Matka (km)"
                              value={row.distance}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "distance",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={row.trafficType === "kayttomaksu"}
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Matkojen lukumäärä (kpl)"
                              value={row.muuTripCount}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "muuTripCount",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={row.trafficType !== "muu"}
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Ajoneuvon paino (t)"
                              value={row.muuVehicleWeight}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "muuVehicleWeight",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={row.trafficType !== "muu"}
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Hyötykuorma (t)"
                              value={row.muuPayload}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "muuPayload",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={row.trafficType !== "muu"}
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Käyttömaksu (€)"
                              value={row.kayttomaksuAmount}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "kayttomaksuAmount",
                                  e.target.value
                                )
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              disabled={row.trafficType !== "kayttomaksu"}
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Kommentit"
                              value={row.comments}
                              onChange={(e) =>
                                handleUnitizationRowChange(
                                  row.id,
                                  "comments",
                                  e.target.value
                                )
                              }
                              fullWidth
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item md={1}>
                            <TextField
                              label="Painoluku (tKm)"
                              value={row.weightValue}
                              type="number"
                              fullWidth
                              disabled
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              color="secondary"
                              onClick={() => handleDeleteUnitizationRow(row.id)}
                              disabled={unitizationRows.length === 1}
                            >
                              <Remove />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                    <Box mt={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        sx={{ mt: 2, mb: 2 }}
                        onClick={handleAddUnitizationRow}
                      >
                        Lisää yksiköinti
                      </Button>
                      <Grid container spacing={2} sx={{ mt: 2, mb: 8 }}>
                        <Grid item xs={6}>
                          <TextField
                            label="Yksiköinnit yhteensä (tKm)"
                            value={unitizationRows
                              .reduce((sum, row) => sum + row.weightValue, 0)
                              .toFixed(3)}
                            disabled
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Käyttömaksut yhteensä (€)"
                            value={unitizationRows
                              .reduce((sum, row) => {
                                const amount =
                                  parseFloat(row.kayttomaksuAmount) || 0;
                                return sum + amount;
                              }, 0)
                              .toFixed(2)}
                            disabled
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mr: 2 }}
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                      >
                        Tallenna
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleBackToForm}
                      >
                        Takaisin
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Confirmation dialog for mode change */}
            <Dialog
              open={showModeChangeDialog}
              onClose={handleCancelModeChange}
              aria-labelledby="mode-change-dialog-title"
              aria-describedby="mode-change-dialog-description"
            >
              <DialogTitle id="mode-change-dialog-title">
                Vaihdetaan yksiköintitilaa
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="mode-change-dialog-description">
                  Vaihtamalla takaisin perustilaan kaikki olemassa olevat
                  yksiköintirivit ja käyttömaksut poistetaan. Haluatko varmasti
                  jatkaa?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelModeChange} color="primary">
                  Peruuta
                </Button>
                <Button
                  onClick={handleConfirmModeChange}
                  color="error"
                  autoFocus
                >
                  Jatka ja poista rivit
                </Button>
              </DialogActions>
            </Dialog>
          </React.Fragment>
        );
      }}
    </Formik>
  );
}
